import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GlobalSearchResponse } from '@utils/types/global-search';

export const GLOBAL_SEARCH_KEY = 'global-search';

/**
 * Below this length we don't hit the server — matches the backend's
 * MIN_QUERY_LENGTH so a 1-char typeahead never round-trips.
 */
export const GLOBAL_SEARCH_MIN_QUERY = 2;

const DEFAULT_LIMIT_PER_CATEGORY = 5;

interface UseGlobalSearchOptions {
  /** The (already debounced) query string. */
  q: string;
  /** Per-category cap; default matches the server default of 5. */
  limitPerCategory?: number;
  /** Allow the caller to gate the query (e.g. dropdown closed). */
  enabled?: boolean;
}

export const useGlobalSearch = ({
  q,
  limitPerCategory = DEFAULT_LIMIT_PER_CATEGORY,
  enabled = true
}: UseGlobalSearchOptions) => {
  const trimmed = q.trim();
  const longEnough = trimmed.length >= GLOBAL_SEARCH_MIN_QUERY;

  return useQuery<GlobalSearchResponse>(
    [GLOBAL_SEARCH_KEY, trimmed, limitPerCategory],
    async ({ signal }) => {
      // React Query passes an AbortSignal here; axios cancels the in-flight
      // request when the next keystroke changes the query key.
      const { data } = await Axios.get<GlobalSearchResponse>('/api/v3/search', {
        params: { q: trimmed, limit_per_category: limitPerCategory },
        signal
      });
      return data;
    },
    {
      enabled: enabled && longEnough,
      // Keep the previous results visible while the next query loads so the
      // dropdown doesn't flicker between empty + populated states on every
      // keystroke.
      keepPreviousData: true,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: false
    }
  );
};
